<template>
  <aside class="fe-account-sidebar">
    <div class="fe-account-sidebar__profile">
      <div class="fe-account-sidebar__avatar" aria-hidden="true">{{ initials }}</div>
      <div class="fe-account-sidebar__meta">
        <span class="fe-account-sidebar__name">{{ displayName }}</span>
        <span class="fe-account-sidebar__email">{{ user.email || '—' }}</span>
      </div>
    </div>
    <nav class="fe-account-nav" aria-label="Account navigation">
      <router-link
        to="/my-account"
        class="fe-account-nav__item"
        :class="{ 'is-active': isDashboardActive }"
      >
        <i class="fas fa-th-large" aria-hidden="true"></i>
        <span>Dashboard</span>
      </router-link>
      <router-link
        to="/my-orders"
        class="fe-account-nav__item"
        :class="{ 'is-active': isOrdersActive }"
      >
        <i class="fas fa-box-open" aria-hidden="true"></i>
        <span>My orders</span>
      </router-link>
      <router-link
        to="/order-tracking"
        class="fe-account-nav__item"
        :class="{ 'is-active': isTrackingActive }"
      >
        <i class="fas fa-shipping-fast" aria-hidden="true"></i>
        <span>Track Order</span>
      </router-link>
      <router-link
        to="/service-tracking"
        class="fe-account-nav__item"
        :class="{ 'is-active': isServiceTrackingActive }"
      >
        <i class="fas fa-screwdriver-wrench" aria-hidden="true"></i>
        <span>Service Tracking</span>
      </router-link>
      <router-link
        to="/my-address"
        class="fe-account-nav__item"
        :class="{ 'is-active': isAddressActive }"
      >
        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
        <span>Addresses</span>
      </router-link>
      <router-link
        to="/wishlist"
        class="fe-account-nav__item"
        :class="{ 'is-active': isWishlistActive }"
      >
        <i class="fas fa-heart" aria-hidden="true"></i>
        <span>Wishlist</span>
      </router-link>
      <router-link
        to="/checkout"
        class="fe-account-nav__item"
        :class="{ 'is-active': isCheckoutActive }"
      >
        <i class="fas fa-shopping-bag" aria-hidden="true"></i>
        <span>Checkout</span>
      </router-link>
    </nav>
    <button
      type="button"
      class="fe-account-nav__item fe-account-nav__item--logout"
      @click="logout"
    >
      <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
      <span>Logout</span>
    </button>
  </aside>
</template>

<script>
import { getAuthToken } from '@/services/axios'
import { useCartStore } from '@/stores/useCartStore'
import { useWishlistStore } from '@/stores/wishlistStore'

function clearShopSession() {
  localStorage.removeItem('user')
  localStorage.removeItem('auth_token')
  sessionStorage.removeItem('auth_token')
}

export default {
  name: 'AccountSidebar',
  data() {
    return {
      user: { name: 'Guest', email: '' },
    }
  },
  computed: {
    displayName() {
      return this.user.name || 'Guest'
    },
    initials() {
      const n = (this.user.name || 'G').trim()
      const parts = n.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return n.slice(0, 2).toUpperCase() || 'G'
    },
    isDashboardActive() {
      return this.$route.path === '/my-account' || this.$route.name === 'MyAccount'
    },
    isOrdersActive() {
      const p = this.$route.path
      return p === '/my-orders' || p.startsWith('/order/')
    },
    isTrackingActive() {
      return this.$route.path === '/order-tracking'
    },
    isServiceTrackingActive() {
      return this.$route.path === '/service-tracking'
    },
    isAddressActive() {
      return this.$route.path === '/my-address' || this.$route.name === 'MyAddress'
    },
    isWishlistActive() {
      return this.$route.path.includes('wishlist')
    },
    isCheckoutActive() {
      return this.$route.path === '/checkout' || this.$route.name === 'Checkout'
    },
  },
  watch: {
    $route() {
      this.refreshUser()
    },
  },
  mounted() {
    this.refreshUser()
  },
  methods: {
    refreshUser() {
      try {
        this.user = JSON.parse(localStorage.getItem('user')) || { name: 'Guest', email: '' }
      } catch {
        this.user = { name: 'Guest', email: '' }
      }
    },
    async logout() {
      const token = getAuthToken()
      try {
        useCartStore().clearSessionAfterLogout()
        useWishlistStore().clearSessionAfterLogout()
      } catch {
        /* ignore */
      }
      if (!token) {
        clearShopSession()
        window.location.href = '#/login'
        window.location.reload()
        return
      }
      try {
        await this.$axios.post('/logout', {})
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Logout API:', error.response?.data?.message || error.message)
        }
      } finally {
        clearShopSession()
        window.location.href = '#/login'
        window.location.reload()
      }
    },
  },
}
</script>

<style scoped>
/* Typography on the container only — do NOT target * (that overrides Font Awesome's
   `.fas` / `.far` font-family on <i> and makes every icon disappear). */
.fe-account-sidebar {
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.fe-account-sidebar__profile {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 1.4rem 1.25rem;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 12%, #fff) 0%,
    #fafbfc 100%
  );
  border-bottom: 1px solid #eef2f6;
}

.fe-account-sidebar__avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.02em;
  color: #ffffff;
  background: var(--fe-accent, #fcbe00);
  box-shadow: 0 4px 14px color-mix(in srgb, var(--fe-accent, #fcbe00) 35%, transparent);
}

.fe-account-sidebar__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fe-account-sidebar__name {
  font-weight: 700;
  font-size: 18px;
  color: #0f172a;
  letter-spacing: -0.005em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fe-account-sidebar__email {
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fe-account-nav {
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0 0.4rem;
}

.fe-account-nav__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0.85rem 1.15rem;
  margin: 0 0.55rem;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #334155;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.005em;
  text-decoration: none;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.fe-account-nav__item i {
  width: 1.2rem;
  text-align: center;
  color: #94a3b8;
  font-size: 17px;
  transition: color 0.2s ease;
}

.fe-account-nav__item:hover {
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 10%, #fff);
  color: #0f172a;
}

.fe-account-nav__item:hover i {
  color: var(--fe-accent, #fcbe00);
}

.fe-account-nav__item.is-active {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 22%, #fff) 0%,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 8%, #fff) 100%
  );
  color: #0f172a;
  box-shadow: inset 3px 0 0 var(--fe-accent, #fcbe00);
  font-weight: 700;
}

.fe-account-nav__item.is-active i {
  color: var(--fe-accent, #d4a000);
}

.fe-account-nav__item--logout {
  margin: 0.4rem 0.55rem 1rem;
  border-top: 1px solid #eef2f6;
  border-radius: 12px;
  padding-top: 0.95rem;
  margin-top: 0.4rem;
  color: #c62828;
  font-weight: 600;
}

.fe-account-nav__item--logout i {
  color: #c62828;
}

.fe-account-nav__item--logout:hover {
  background: #fff5f5;
  color: #b71c1c;
}

@media (max-width: 767.98px) {
  .fe-account-sidebar {
    margin-bottom: 0.5rem;
  }
}
</style>